import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon, ChevronDownIcon, CheckIcon, FolderPlusIcon } from '@heroicons/react/24/outline';
import {
    useCreateFromListingMutation,
    useCheckListingInClosetQuery,
    useRemoveFromClosetMutation,
    useGetClosetsQuery,
    useCreateClosetMutation
} from '../../redux/apis';
import { Label } from 'semantic-ui-react';
import AuthRequiredModal from '../common/AuthRequiredModal';

// SUSU Closet icon URL
const SUSU_ICON = 'https://res.cloudinary.com/sheyou/image/upload/v1769555022/closet_icon_a208ddfe7d.png';

/**
 * AddToClosetButton - Split button for adding/removing SHEYOU listings to user's closet
 * 
 * Design: [+ 🏠 My Closet ▼] or [✓ 🏠 Closet Name ▼] when added
 * - Click + or "My Closet" → Toggle: Add to default/last closet OR Remove from all closets
 * - Click ▼ → Open closet selector dropdown with per-closet checkmarks
 */
const AddToClosetButton = ({ listingId, className = '' }) => {
    const currentUser = useSelector(state => state.auth?.user);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [toastMessage, setToastMessage] = useState(null); // { text, isError }
    const [newClosetName, setNewClosetName] = useState('');
    const [isCreatingCloset, setIsCreatingCloset] = useState(false);
    const [addedToCloset, setAddedToCloset] = useState(null); // { id, name } - tracks main display
    const [defaultClosetName, setDefaultClosetName] = useState('My Closet'); // Display name when not in closet
    const dropdownRef = useRef(null);

    const [createFromListing, { isLoading: isAdding }] = useCreateFromListingMutation();
    const [removeFromCloset, { isLoading: isRemoving }] = useRemoveFromClosetMutation();

    // Check if listing is already in closet on mount (only for logged-in users)
    const { data: closetStatus, isLoading: isCheckingCloset } = useCheckListingInClosetQuery(listingId, {
        skip: !currentUser || !listingId
    });

    const { data: closets = [], isLoading: isLoadingClosets } = useGetClosetsQuery(undefined, {
        skip: !currentUser || !showDropdown
    });
    const [createCloset] = useCreateClosetMutation();

    // Set interactive state based on closet check
    useEffect(() => {
        if (closetStatus) {
            if (closetStatus.inCloset) {
                // If in closets, determine which one to show on main button
                const lastUsedId = closetStatus.lastCloset?.id;
                // Prefer last used if it's one of the closets this item is in
                const activeId = closetStatus.closetIds.includes(parseInt(lastUsedId))
                    ? lastUsedId
                    : closetStatus.closetIds[0];

                const index = closetStatus.closetIds.indexOf(parseInt(activeId));
                const activeName = index !== -1 ? closetStatus.closetNames[index] : 'My Closet';

                setAddedToCloset({ id: activeId, name: activeName });
            } else {
                setAddedToCloset(null);
            }

            // Set default closet name from lastCloset preference (the one used for quick-add)
            if (closetStatus.lastCloset) {
                setDefaultClosetName(closetStatus.lastCloset.name);
            }
        }
    }, [closetStatus]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
                setIsCreatingCloset(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Clear toast message after delay
    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => setToastMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    /**
     * Handle main button click - toggles item in closet
     */
    const handleQuickAction = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        try {
            if (addedToCloset) {
                // TOGGLE: REMOVE from ALL closets (delete the item)
                await removeFromCloset({ listingId }).unwrap();
                setToastMessage({ text: 'Removed from closet', isError: false });
            } else {
                // TOGGLE: ADD to last used/default closet
                const closetId = closetStatus?.lastCloset?.id || null;
                const result = await createFromListing({ listingId, closetId }).unwrap();
                const closetName = result.closetName || defaultClosetName;
                setToastMessage({ text: `Added to ${closetName}!`, isError: false });
            }
        } catch (error) {
            console.error('Error toggling closet state:', error);
            setToastMessage({ text: 'Failed to update closet', isError: true });
        }
    };

    /**
     * Handle individual closet toggle from dropdown
     */
    const handleToggleInCloset = async (closetId, closetName) => {
        try {
            const isCurrentlyInThisCloset = closetStatus?.closetIds?.includes(parseInt(closetId));

            if (isCurrentlyInThisCloset) {
                // REMOVE from this specific closet
                await removeFromCloset({ listingId, closetId }).unwrap();
                setToastMessage({ text: `Removed from ${closetName}`, isError: false });
            } else {
                // ADD to this specific closet
                await createFromListing({ listingId, closetId }).unwrap();
                setToastMessage({ text: `Added to ${closetName}!`, isError: false });
            }
        } catch (error) {
            console.error('Error toggling closet item:', error);
            setToastMessage({ text: 'Failed to update closet', isError: true });
        }
    };

    const handleCreateCloset = async (e) => {
        e.preventDefault();
        if (!newClosetName.trim()) return;

        try {
            const newCloset = await createCloset(newClosetName.trim()).unwrap();
            await createFromListing({ listingId, closetId: newCloset.id }).unwrap();
            setToastMessage({ text: `Created & Added to ${newCloset.name}!`, isError: false });
            setNewClosetName('');
            setIsCreatingCloset(false);
            setShowDropdown(false);
        } catch (error) {
            console.error('Error creating closet:', error);
            setToastMessage({ text: 'Failed to create closet', isError: true });
        }
    };

    const handleDropdownClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        setShowDropdown(!showDropdown);
    };

    // UI state determination
    const buttonText = addedToCloset ? addedToCloset.name : defaultClosetName;
    const isInCloset = !!addedToCloset;
    const isLoading = isAdding || isRemoving || isCheckingCloset;

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Toast Message - Positioned ON TOP with pointing below */}
            {toastMessage && (
                <div className="absolute bottom-full left-0 mb-2 whitespace-nowrap z-50 animate-in fade-in slide-in-from-bottom-1">
                    <Label
                        basic
                        color={toastMessage.isError ? 'red' : 'teal'}
                        pointing="below"
                        size="small"
                        className="shadow-md"
                    >
                        {toastMessage.text}
                    </Label>
                </div>
            )}

            {/* Split Button Container */}
            <div className={`flex items-center rounded-lg border overflow-hidden transition-all duration-200 ${isInCloset ? 'border-brand-teal bg-brand-teal' : 'border-brand-teal bg-white'}`}>
                {/* Main Action Button (Toggle) */}
                <button
                    onClick={handleQuickAction}
                    disabled={isLoading}
                    className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors text-sm font-medium ${isInCloset ? 'text-white' : 'text-brand-teal hover:bg-brand-teal-light-hover'}`}
                >
                    {isInCloset ? (
                        <CheckIcon className="w-4 h-4 stroke-[2.5]" />
                    ) : (
                        <PlusIcon className="w-4 h-4 stroke-[2.5]" />
                    )}
                    <span className="hidden sm:inline">{buttonText}</span>
                    {isLoading && (
                        <span className="ml-1 animate-spin text-xs">⟳</span>
                    )}
                </button>

                {/* Dropdown Toggle */}
                <button
                    onClick={handleDropdownClick}
                    className={`px-2 py-1.5 border-l transition-colors ${isInCloset ? 'border-white/30 text-white hover:bg-white/10' : 'border-brand-teal text-brand-teal hover:bg-brand-teal-light-hover'}`}
                >
                    <ChevronDownIcon className="w-4 h-4 stroke-[2.5]" />
                </button>
            </div>


            {/* Dropdown Menu */}
            {showDropdown && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                    <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Add to Closet</p>
                    </div>

                    <div className="max-h-48 overflow-y-auto custom-scrollbar">
                        {isLoadingClosets ? (
                            <div className="p-4 text-center text-gray-400 text-sm italic">Loading...</div>
                        ) : closets.length === 0 ? (
                            <div className="p-4 text-center text-gray-400 text-sm">No closets found</div>
                        ) : (
                            closets.map((closet) => {
                                const isAddedHere = closetStatus?.closetIds?.includes(parseInt(closet.id));
                                return (
                                    <button
                                        key={closet.id}
                                        onClick={() => handleToggleInCloset(closet.id, closet.name)}
                                        className="w-full px-3 py-2 text-left hover:bg-brand-teal/5 flex items-center justify-between group transition-colors"
                                    >
                                        <div className="flex items-center gap-2 flex-1 truncate">
                                            <span className={`text-sm ${isAddedHere ? 'text-brand-teal font-semibold' : 'text-gray-600'}`}>
                                                {closet.name}
                                            </span>
                                            {closet.isDefault && (
                                                <span className="text-[10px] text-gray-300 font-medium">(default)</span>
                                            )}
                                        </div>
                                        {isAddedHere && (
                                            <CheckIcon className="w-4 h-4 text-brand-teal stroke-[3]" />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <div className="border-t border-gray-100 bg-gray-50/30">
                        {isCreatingCloset ? (
                            <form onSubmit={handleCreateCloset} className="p-2">
                                <input
                                    type="text"
                                    value={newClosetName}
                                    onChange={(e) => setNewClosetName(e.target.value)}
                                    placeholder="New closet name..."
                                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:border-brand-teal focus:ring-1 focus:ring-brand-teal outline-none transition-all"
                                    autoFocus
                                />
                                <div className="flex gap-2 mt-2">
                                    <button
                                        type="submit"
                                        disabled={!newClosetName.trim()}
                                        className="flex-1 px-2 py-1 text-xs bg-brand-teal text-white rounded hover:bg-brand-teal-dark transition-colors font-medium disabled:opacity-50"
                                    >
                                        Create
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsCreatingCloset(false)}
                                        className="flex-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <button
                                onClick={() => setIsCreatingCloset(true)}
                                className="w-full px-3 py-2 text-left hover:bg-brand-teal/5 flex items-center gap-2 text-brand-teal/80 hover:text-brand-teal transition-colors"
                            >
                                <FolderPlusIcon className="w-5 h-5" />
                                <span className="text-sm font-semibold">New Closet</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Auth Modal */}
            <AuthRequiredModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                message="Please log in to save items to your closet"
                actionType="save to closet"
            />
        </div>
    );
};

export default AddToClosetButton;
